// @file Surface attachments store
import { getVuexStore } from '@@/bits/pinia'
import type { Cid } from '@@/types'
import type { RootState } from '@@/vuexstore/surface/types'
import type { BeethovenDisplayAttributes } from '@padlet/beethoven-client'
import { defineStore } from 'pinia'
import { computed } from 'vue'

export const useSurfaceAttachmentsStore = defineStore('surfaceAttachments', () => {
  const vuexStore = getVuexStore<RootState>()

  /* ---------------------- */
  /* STATE                  */
  /* ---------------------- */
  const fileUploadingToPickerBox = computed(() => vuexStore?.getters['attachment/fileUploadingToPickerBox'])
  const linkDisplayAttributes = computed(() => vuexStore?.state.attachment.linkDisplayAttributes)
  const surfacePreviewImageLoaded = computed(() => vuexStore?.getters['attachment/surfacePreviewImageLoaded'])

  /* ---------------------- */
  /* GETTERS                */
  /* ---------------------- */
  const getLinkDisplayAttributesForUrl = (url: string): BeethovenDisplayAttributes | null => {
    return vuexStore?.getters['attachment/linkDisplayAttributesForUrl'](url) ?? null
  }

  const getLinkDisplayAttributesForPost = (postCid: string): BeethovenDisplayAttributes | null => {
    return vuexStore?.getters['attachment/linkDisplayAttributesForPost'](postCid) ?? null
  }

  const isSurfacePreviewImageLoadedForUrl = (url: string): boolean => {
    return vuexStore?.getters['attachment/isSurfacePreviewImageLoadedForUrl'](url) ?? false
  }

  /* ---------------------- */
  /* ACTIONS                */
  /* ---------------------- */
  const uploadFileToPickerBox = (file: File | null): void => {
    void vuexStore?.dispatch('attachment/uploadFileToPickerBox', { file })
  }
  const completeUploadingFileToPickerBox = (): void => {
    void vuexStore?.dispatch('attachment/completeUploadingFileToPickerBox')
  }

  const cancelUploadFileToPickerBox = (): void => {
    void vuexStore?.dispatch('attachment/cancelUploadFileToPickerBox')
  }

  const updateLinkDisplayAttributes = (payload): void => {
    void vuexStore?.dispatch('attachment/updateLinkDisplayAttributes', payload)
  }

  const setSurfacePreviewImageLoaded = (url?: string): void => {
    void vuexStore?.dispatch('attachment/setSurfacePreviewImageLoaded', { url })
  }

  const fetchDisplayAttributesForPost = async (postCid: Cid): Promise<void> => {
    await vuexStore?.dispatch('attachment/fetchDisplayAttributesForPost', postCid)
  }

  return {
    // state
    fileUploadingToPickerBox,
    linkDisplayAttributes,
    surfacePreviewImageLoaded,

    // actions
    getLinkDisplayAttributesForUrl,
    getLinkDisplayAttributesForPost,
    isSurfacePreviewImageLoadedForUrl,

    // actions
    uploadFileToPickerBox,
    cancelUploadFileToPickerBox,
    completeUploadingFileToPickerBox,
    updateLinkDisplayAttributes,
    setSurfacePreviewImageLoaded,
    fetchDisplayAttributesForPost,
  }
})
